import Card from "components/card";
import InputField from "components/fields/InputField";
import React, { useState, useRef, useEffect, useMemo } from "react";
import axios from "axios";
import { MdEmail, MdPhone, MdSource, MdCalendarToday, MdFlagCircle, MdChecklist, MdFolder } from "react-icons/md";
import { IoChevronDown } from "react-icons/io5";
import { Country, State, City } from "country-state-city";
import { API_BASE_URL } from "utils/api";
import { getToken } from "utils/auth";

const overviewItems = [
  { label: "Email", key: "email", icon: MdEmail, editable: true, type: "email" },
  { label: "Phone", key: "phone", icon: MdPhone, editable: true, type: "tel" },
  { label: "Source", key: "source", icon: MdSource, editable: true, type: "select" },
  { label: "Status", key: "status", icon: MdChecklist, editable: true, type: "select" },
  { label: "Priority", key: "priority", icon: MdFlagCircle, editable: true, type: "select" },
  { label: "Folder", key: "folderId", icon: MdFolder, editable: true, type: "folder" },
  { label: "Created Date", key: "createdAt", icon: MdCalendarToday, editable: false, type: "date" },
];

const addressItems = [
  { label: "House Name/Number", key: "houseNameOrNumber", placeholder: "House name or number" },
  { label: "Locality/Landmark", key: "localityOrLandmark", placeholder: "Locality or landmark" },
  { label: "Pin Code", key: "pinCode", placeholder: "PIN code" },
  { label: "Country", key: "country", placeholder: "Country" },
  { label: "State", key: "state", placeholder: "State" },
  { label: "District", key: "district", placeholder: "District" },
];

const sourceOptions = [
  "Website", "Referral", "Ads", "Social Media", "Email", "Phone", "Direct", "Walk-in", "Other"
];
const statusOptions = [
  "New", "Contacted", "Proposal", "Negotiation", "Closed", "Follow-Up", "Lost"
];
const priorityOptions = ["Low", "Medium", "High", "Urgent"];

const ClientOverview = ({ client, saveClientSection }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [localData, setLocalData] = useState({
    email: "",
    phone: "",
    source: "",
    status: "",
    priority: "",
    createdAt: "",
    houseNameOrNumber: "",
    localityOrLandmark: "",
    pinCode: "",
    district: "",
    state: "",
    country: "",
    folderId: "",
    folderName: "",
  });
  const [folderSearchTerm, setFolderSearchTerm] = useState("");
  const [folders, setFolders] = useState([]);
  const [folderPage, setFolderPage] = useState(1);
  const [isFoldersLoading, setIsFoldersLoading] = useState(false);
  const [pinError, setPinError] = useState("");
  const [showFolderDropdown, setShowFolderDropdown] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState({});
  const folderDropdownRef = useRef(null);
  const dropdownRefs = useRef({});
  const [searchByDropdown, setSearchByDropdown] = useState({});

  useEffect(() => {
    if (!isEditing) {
      setLocalData({
        email: client?.email || "",
        phone: client?.phone || "",
        source: client?.source || "",
        status: client?.status || "",
        priority: client?.priority || "",
        createdAt: client?.createdAt || client?.createdDate || "",
        houseNameOrNumber: client?.houseNameOrNumber || "",
        localityOrLandmark: client?.localityOrLandmark || "",
        pinCode: client?.pinCode || "",
        district: client?.district || "",
        state: client?.state || "",
        country: client?.country || "",
        folderId: client?.folderId?._id || client?.folderId || "",
        folderName: client?.folderId?.name || "",
      });
      setFolderSearchTerm(client?.folderId?.name || "");
      setFolders([]);
      setFolderPage(1);
      setShowFolderDropdown(false);
    }
  }, [client, isEditing]);

  const countryOptions = useMemo(() => Country.getAllCountries().map((c) => c.name), []);
  const selectedCountry = useMemo(
    () => Country.getAllCountries().find((c) => c.name === (localData.country || client?.country)),
    [localData.country, client?.country],
  );
  const stateOptions = useMemo(() => {
    if (!selectedCountry) return [];
    return State.getStatesOfCountry(selectedCountry.isoCode).map((s) => s.name);
  }, [selectedCountry]);
  const selectedState = useMemo(() => {
    if (!selectedCountry) return null;
    const stateName = localData.state || client?.state;
    if (!stateName) return null;
    return State.getStatesOfCountry(selectedCountry.isoCode).find((s) => s.name === stateName);
  }, [selectedCountry, localData.state, client?.state]);
  const districtOptions = useMemo(() => {
    if (!selectedCountry || !selectedState) return [];
    const cities = City.getCitiesOfState(selectedCountry.isoCode, selectedState.isoCode);
    return Array.from(new Set(cities.map((city) => city.name))).sort();
  }, [selectedCountry, selectedState]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (folderDropdownRef.current && !folderDropdownRef.current.contains(event.target)) {
        setShowFolderDropdown(false);
      }

      const hasOpenDropdown = Object.values(dropdownOpen).some(Boolean);
      if (hasOpenDropdown) {
        const clickedOutsideAnyDropdown = Object.keys(dropdownOpen).some((key) => {
          const dropdownRef = dropdownRefs.current[key];
          return dropdownOpen[key] && dropdownRef && !dropdownRef.contains(event.target);
        });

        if (clickedOutsideAnyDropdown) {
          setDropdownOpen({});
        }
      }
    };

    if (showFolderDropdown || Object.values(dropdownOpen).some(Boolean)) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showFolderDropdown, dropdownOpen]);

  const resetLocalData = () => {
    setLocalData({
      email: client?.email || "",
      phone: client?.phone || "",
      source: client?.source || "",
      status: client?.status || "",
      priority: client?.priority || "",
      createdAt: client?.createdAt || client?.createdDate || "",
      houseNameOrNumber: client?.houseNameOrNumber || "",
      localityOrLandmark: client?.localityOrLandmark || "",
      pinCode: client?.pinCode || "",
      district: client?.district || "",
      state: client?.state || "",
      country: client?.country || "",
      folderId: client?.folderId?._id || client?.folderId || "",
      folderName: client?.folderId?.name || "",
    });
    setFolderSearchTerm(client?.folderId?.name || "");
    setFolders([]);
    setFolderPage(1);
    setShowFolderDropdown(false);
    setDropdownOpen({});
  };

  const handleFieldChange = (key, value) => {
    if (key === "pinCode") {
      setPinError("");
    }
    if (key === "houseNameOrNumber") {
      value = value.slice(0, 30);
    }
    if (key === "localityOrLandmark") {
      value = value.slice(0, 40);
    }
    setLocalData((prev) => ({ ...prev, [key]: value }));
  };

  const handleCancel = () => {
    resetLocalData();
    setPinError("");
    setIsEditing(false);
  };

  const formatValue = (key) => {
    if (key === "createdAt") {
      const value = localData.createdAt || client?.createdAt || client?.createdDate;
      if (!value) return "Not available";
      const date = new Date(value);
      return Number.isNaN(date.getTime())
        ? String(value)
        : date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    }
    if (key === "folderId") {
      return isEditing
        ? localData.folderName || client?.folderId?.name || "Not assigned"
        : client?.folderId?.name || localData.folderName || "Not assigned";
    }
    if (!isEditing) {
      return client?.[key] || localData[key] || "Not available";
    }
    return localData[key] || client?.[key] || "Not available";
  };

  const getOptions = (key) => {
    if (key === "source") return sourceOptions;
    if (key === "status") return statusOptions;
    if (key === "priority") return priorityOptions;
    return [];
  };

  const getPlaceholder = (key) => {
    if (key === "source") return "Select source";
    if (key === "status") return "Select status";
    if (key === "priority") return "Select priority";
    return "Select option";
  };

  const searchFolders = async (searchTerm = "", page = 1) => {
    try {
      setIsFoldersLoading(true);
      const token = localStorage.getItem("token") || getToken();
      const response = await axios.get(`${API_BASE_URL}/folders/search`, {
        params: {
          q: searchTerm,
          page,
          type: "Client",
          limit: 10,
        },
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      });

      const data = response.data?.data;
      if (!data) {
        setFolders([]);
        return;
      }

      const matched = data.matched?.data || [];
      const remaining = data.remaining?.data || [];
      setFolders(page === 1 ? [...matched, ...remaining] : [...folders, ...remaining]);
      setFolderPage(page);
    } catch (error) {
      console.error("Error searching folders:", error);
      setFolders([]);
    } finally {
      setIsFoldersLoading(false);
    }
  };

  const handleFolderSearch = (value) => {
    setFolderSearchTerm(value);
    setShowFolderDropdown(true);
    setLocalData((prev) => ({
      ...prev,
      folderId: "",
      folderName: value.trim() ? prev.folderName : "",
    }));

    if (!value.trim()) {
      searchFolders("", 1);
      return;
    }
    searchFolders(value, 1);
  };

  const handleFolderSelect = (folder) => {
    setLocalData((prev) => ({
      ...prev,
      folderId: folder._id,
      folderName: folder.name,
    }));
    setFolderSearchTerm(folder.name);
    setShowFolderDropdown(false);
  };

  const handleFolderDropdownScroll = (event) => {
    const element = event.target;
    if (
      element.scrollHeight - element.scrollTop <= element.clientHeight + 50 &&
      !isFoldersLoading
    ) {
      searchFolders(folderSearchTerm, folderPage + 1);
    }
  };

  const handleSave = async () => {
    if (localData.pinCode && !/^\d{6}$/.test(localData.pinCode)) {
      setPinError("Pin code must be 6 digits");
      return;
    }
    setPinError("");
    setIsSaving(true);
    try {
      await saveClientSection(
        {
          email: localData.email,
          phone: localData.phone,
          source: localData.source,
          status: localData.status,
          priority: localData.priority,
          houseNameOrNumber: localData.houseNameOrNumber,
          localityOrLandmark: localData.localityOrLandmark,
          pinCode: localData.pinCode,
          district: localData.district,
          state: localData.state,
          country: localData.country,
          folderId: localData.folderId,
        },
        "Client Overview"
      );
      setIsEditing(false);
    } catch (error) {
      // Error is handled by saveClientSection
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card extra="w-full rounded-[24px] border border-gray-200 p-6 shadow-sm dark:border-white/10 dark:bg-navy-700">
      <div className="mb-5 flex items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-navy-900 dark:text-white">Client Overview</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">Core contact data grouped for fast decisions.</p>
        </div>
        <div className="flex items-center gap-2">
          {isEditing ? (
            <>
              <button
                onClick={handleCancel}
                disabled={isSaving}
                className="inline-flex items-center gap-2 rounded-2xl bg-slate-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-600 disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="inline-flex items-center gap-2 rounded-2xl bg-emerald-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-600 disabled:opacity-60"
              >
                {isSaving ? "Saving..." : "Save"}
              </button>
            </>
          ) : (
            <button
              onClick={() => setIsEditing(true)}
              className="inline-flex items-center gap-2 rounded-2xl bg-brand-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-brand-600"
            >
              Edit
            </button>
          )}
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {overviewItems.map((item) => {
          const Icon = item.icon;
          return (
            <div key={item.key} className="flex gap-4 rounded-3xl border border-gray-200 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-navy-800">
              <div className="grid h-11 w-11 place-items-center rounded-2xl bg-brand-50 text-brand-500 dark:bg-brand-500/10 dark:text-brand-300">
                <Icon className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs uppercase tracking-[0.24em] text-gray-400">{item.label}</p>
                {isEditing && item.editable ? (
                  item.type === "select" && ["source", "status", "priority"].includes(item.key) ? (
                    <div className="relative mt-2" ref={(el) => (dropdownRefs.current[item.key] = el)}>
                      <button
                        type="button"
                        className="flex w-full items-center justify-between rounded-2xl border border-gray-200 bg-white px-3 py-2 text-sm text-navy-700 outline-none transition hover:border-brand-500 focus:border-brand-500 dark:border-white/10 dark:bg-navy-700 dark:text-white h-11 min-h-0"
                        onClick={() => setDropdownOpen((prev) => ({ ...prev, [item.key]: !prev[item.key] }))}
                      >
                        <span>
                          {localData[item.key] || client?.[item.key] || getPlaceholder(item.key)}
                        </span>
                        <svg className={`ml-2 h-4 w-4 transition-transform ${dropdownOpen[item.key] ? "rotate-180" : "rotate-0"}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      {dropdownOpen[item.key] && (
                        <div className="absolute left-0 right-0 z-50 mt-2 max-h-60 overflow-y-auto rounded-2xl border border-gray-200 bg-white shadow-lg dark:border-white/10 dark:bg-navy-700">
                          <div className="px-3 py-2 text-gray-400 text-xs uppercase tracking-widest">{getPlaceholder(item.key)}</div>
                          {getOptions(item.key).map((option) => (
                            <div
                              key={option}
                              className={`cursor-pointer px-4 py-2 text-sm hover:bg-brand-50 dark:hover:bg-brand-500/10 ${(localData[item.key] || client?.[item.key]) === option
                                  ? "bg-brand-50 dark:bg-brand-500/10 font-semibold"
                                  : ""
                                }`}
                              onClick={() => {
                                handleFieldChange(item.key, option);
                                setDropdownOpen((prev) => ({ ...prev, [item.key]: false }));
                              }}
                            >
                              {option}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : item.type === "folder" ? (
                    <div className="relative mt-2" ref={folderDropdownRef}>
                      <InputField
                        id="client-folder"
                        label=""
                        type="text"
                        placeholder="Search folder..."
                        value={folderSearchTerm}
                        onFocus={() => handleFolderSearch(folderSearchTerm)}
                        onChange={(e) => handleFolderSearch(e.target.value)}
                        extra="mt-2 w-full"
                      />
                      {showFolderDropdown && (
                        <div
                          className="absolute left-0 right-0 z-50 mt-2 max-h-60 overflow-y-auto rounded-2xl border border-gray-200 bg-white shadow-lg dark:border-white/10 dark:bg-navy-700"
                          onScroll={handleFolderDropdownScroll}
                        >
                          <button
                            type="button"
                            className="w-full text-left px-4 py-2 text-sm text-navy-700 hover:bg-brand-50 dark:text-white dark:hover:bg-brand-500/10"
                            onClick={() => {
                              setLocalData((prev) => ({
                                ...prev,
                                folderId: "",
                                folderName: "",
                              }));
                              setFolderSearchTerm("");
                              setShowFolderDropdown(false);
                            }}
                          >
                            None
                          </button>
                          {isFoldersLoading ? (
                            <div className="p-4 text-sm text-gray-500 dark:text-gray-400">Searching folders...</div>
                          ) : folders.length === 0 ? (
                            <div className="p-4 text-sm text-gray-500 dark:text-gray-400">No folders found</div>
                          ) : (
                            folders.map((folder) => (
                              <button
                                key={folder._id}
                                type="button"
                                className="w-full text-left px-4 py-2 text-sm text-navy-700 hover:bg-brand-50 dark:text-white dark:hover:bg-brand-500/10"
                                onClick={() => handleFolderSelect(folder)}
                              >
                                {folder.name}
                              </button>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <InputField
                      id={`client-${item.key}`}
                      label=""
                      type={item.type}
                      placeholder={`Enter ${item.label.toLowerCase()}`}
                      value={localData[item.key]}
                      onChange={(e) => handleFieldChange(item.key, e.target.value)}
                      extra="mt-2"
                    />
                  )
                ) : (
                  <p className="mt-2 text-base font-semibold text-navy-900 dark:text-white">{formatValue(item.key)}</p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-6">
        <h3 className="mb-4 text-lg font-semibold text-navy-900 dark:text-white">Address Information</h3>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {addressItems.map((item) => (
            <div key={item.key} className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-navy-800">
              <label className="text-xs uppercase tracking-[0.24em] text-gray-400">{item.label}</label>
              {isEditing ? (
                (item.key === "country") ? (
                  <div className="relative mt-2 w-full" ref={(el) => (dropdownRefs.current["country"] = el)}>
                    <button
                      type="button"
                      onClick={() => setDropdownOpen((prev) => ({ ...prev, country: !prev.country }))}
                      className={`flex h-11 w-full items-center justify-between rounded-2xl border px-3 text-left text-sm ${dropdownOpen.country ? "border-brand-500" : "border-gray-200"} bg-white dark:border-white/10 dark:bg-navy-700`}
                    >
                      <span className="truncate">{localData.country || client?.country || "Select country"}</span>
                      <IoChevronDown className="h-4 w-4 text-gray-500" />
                    </button>
                    {dropdownOpen.country && (
                      <div className="absolute top-full z-50 mt-2 max-h-44 w-full overflow-y-auto rounded-2xl border border-gray-200 bg-white p-2 shadow-lg dark:border-white/10 dark:bg-navy-700">
                        <input
                          type="text"
                          value={searchByDropdown.country || ""}
                          onChange={(e) => setSearchByDropdown((prev) => ({ ...prev, country: e.target.value }))}
                          placeholder={`Search country`}
                          className="mb-2 h-9 w-full rounded-md border border-gray-200 px-2 text-sm outline-none focus:border-brand-500 dark:border-white/10 dark:bg-gray-700 dark:text-white"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            handleFieldChange("country", "");
                            handleFieldChange("state", "");
                            handleFieldChange("district", "");
                            setDropdownOpen((prev) => ({ ...prev, country: false }));
                          }}
                          className="mb-1 h-8 w-full rounded-md px-2 text-left text-sm text-gray-500 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                        >
                          Clear
                        </button>
                        {countryOptions.filter((opt) => (searchByDropdown.country || "").trim() === "" ? true : opt.toLowerCase().includes((searchByDropdown.country || "").trim().toLowerCase())).map((option) => (
                          <button
                            key={option}
                            type="button"
                            onClick={() => {
                              handleFieldChange("country", option);
                              handleFieldChange("state", "");
                              handleFieldChange("district", "");
                              setDropdownOpen((prev) => ({ ...prev, country: false }));
                            }}
                            className={`h-8 w-full rounded-md px-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 ${(localData.country || client?.country) === option ? "bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400" : "text-gray-700 dark:text-gray-200"}`}
                          >
                            {option}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (item.key === "state") ? (
                  <div className="relative mt-2 w-full" ref={(el) => (dropdownRefs.current["state"] = el)}>
                    <button
                      type="button"
                      onClick={() => setDropdownOpen((prev) => ({ ...prev, state: !prev.state }))}
                      className={`flex h-11 w-full items-center justify-between rounded-2xl border px-3 text-left text-sm ${dropdownOpen.state ? "border-brand-500" : "border-gray-200"} bg-white dark:border-white/10 dark:bg-navy-700`}
                    >
                      <span className="truncate">{localData.state || client?.state || "Select state"}</span>
                      <IoChevronDown className="h-4 w-4 text-gray-500" />
                    </button>
                    {dropdownOpen.state && (
                      <div className="absolute top-full z-50 mt-2 max-h-44 w-full overflow-y-auto rounded-2xl border border-gray-200 bg-white p-2 shadow-lg dark:border-white/10 dark:bg-navy-700">
                        <input
                          type="text"
                          value={searchByDropdown.state || ""}
                          onChange={(e) => setSearchByDropdown((prev) => ({ ...prev, state: e.target.value }))}
                          placeholder={`Search state`}
                          className="mb-2 h-9 w-full rounded-md border border-gray-200 px-2 text-sm outline-none focus:border-brand-500 dark:border-white/10 dark:bg-gray-700 dark:text-white"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            handleFieldChange("state", "");
                            handleFieldChange("district", "");
                            setDropdownOpen((prev) => ({ ...prev, state: false }));
                          }}
                          className="mb-1 h-8 w-full rounded-md px-2 text-left text-sm text-gray-500 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                        >
                          Clear
                        </button>
                        {stateOptions.filter((opt) => (searchByDropdown.state || "").trim() === "" ? true : opt.toLowerCase().includes((searchByDropdown.state || "").trim().toLowerCase())).map((option) => (
                          <button
                            key={option}
                            type="button"
                            onClick={() => {
                              handleFieldChange("state", option);
                              handleFieldChange("district", "");
                              setDropdownOpen((prev) => ({ ...prev, state: false }));
                            }}
                            className={`h-8 w-full rounded-md px-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 ${(localData.state || client?.state) === option ? "bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400" : "text-gray-700 dark:text-gray-200"}`}
                          >
                            {option}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (item.key === "district") ? (
                  <div className="relative mt-2 w-full" ref={(el) => (dropdownRefs.current["district"] = el)}>
                    <button
                      type="button"
                      onClick={() => setDropdownOpen((prev) => ({ ...prev, district: !prev.district }))}
                      className={`flex h-11 w-full items-center justify-between rounded-2xl border px-3 text-left text-sm ${dropdownOpen.district ? "border-brand-500" : "border-gray-200"} bg-white dark:border-white/10 dark:bg-navy-700`}
                    >
                      <span className="truncate">{localData.district || client?.district || "Select district"}</span>
                      <IoChevronDown className="h-4 w-4 text-gray-500" />
                    </button>
                    {dropdownOpen.district && (
                      <div className="absolute top-full z-50 mt-2 max-h-44 w-full overflow-y-auto rounded-2xl border border-gray-200 bg-white p-2 shadow-lg dark:border-white/10 dark:bg-navy-700">
                        <input
                          type="text"
                          value={searchByDropdown.district || ""}
                          onChange={(e) => setSearchByDropdown((prev) => ({ ...prev, district: e.target.value }))}
                          placeholder={`Search district`}
                          className="mb-2 h-9 w-full rounded-md border border-gray-200 px-2 text-sm outline-none focus:border-brand-500 dark:border-white/10 dark:bg-gray-700 dark:text-white"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            handleFieldChange("district", "");
                            setDropdownOpen((prev) => ({ ...prev, district: false }));
                          }}
                          className="mb-1 h-8 w-full rounded-md px-2 text-left text-sm text-gray-500 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                        >
                          Clear
                        </button>
                        {districtOptions.filter((opt) => (searchByDropdown.district || "").trim() === "" ? true : opt.toLowerCase().includes((searchByDropdown.district || "").trim().toLowerCase())).map((option) => (
                          <button
                            key={option}
                            type="button"
                            onClick={() => {
                              handleFieldChange("district", option);
                              setDropdownOpen((prev) => ({ ...prev, district: false }));
                            }}
                            className={`h-8 w-full rounded-md px-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 ${(localData.district || client?.district) === option ? "bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400" : "text-gray-700 dark:text-gray-200"}`}
                          >
                            {option}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    <InputField
                      id={`client-${item.key}`}
                      label=""
                      type="text"
                      placeholder={item.placeholder}
                      value={localData[item.key]}
                      onChange={(e) => handleFieldChange(item.key, e.target.value)}
                      maxLength={item.key === "houseNameOrNumber" || item.key === "localityOrLandmark" ? 40 : undefined}
                      extra="mt-2"
                    />
                    {item.key === "pinCode" && pinError && (
                      <p className="mt-1 text-sm font-medium text-red-500">{pinError}</p>
                    )}
                  </div>
                )
              ) : (
                <p className="mt-2 text-base font-semibold text-navy-900 dark:text-white">
                  {localData[item.key] || client?.[item.key] || "—"}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
};

export default ClientOverview;
